import { updateReapproAlgorithmParams, useReapproAlgorithmParams } from '@gozoki/api';
import {
    REAPPRO_ALGORITHM_BY_ID,
    REAPPRO_ALGORITHM_IDS,
    ReapproAlgorithmParamUpdate,
} from '@gozoki/api-types';
import { Button, TextField, Typography } from '@mui/material';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useToast } from '../../components/communication/Toast';
import { parseIntOrNull } from '../../utils/types/number';

type ReapproAlgorithmParamsPanelProps = {
    isUserAdmin: boolean;
};

const ReapproAlgorithmParamsPanel = ({ isUserAdmin }: ReapproAlgorithmParamsPanelProps) => {
    const [Toast, showToast] = useToast();
    const queryClient = useQueryClient();
    const { data: algoParams = [] } = useReapproAlgorithmParams();

    useEffect(() => {
        setParams(
            algoParams.map((param) => ({
                id: REAPPRO_ALGORITHM_IDS[param.algorithm],
                daysBetweenDeliveries: param.daysBetweenDeliveries,
            }))
        );
    }, [algoParams]);

    const [params, setParams] = useState<ReapproAlgorithmParamUpdate[]>([]);

    const updateAlgoDaysMut = useMutation({
        mutationFn: updateReapproAlgorithmParams,
        onSuccess: () => {
            showToast({
                message: 'Paramétrage réappro global mis à jour',
                severity: 'success',
            });
            queryClient.invalidateQueries({ queryKey: ['reapproAlgorithmParams'] });
        },
        onError: () => {
            showToast({
                message: "Impossible d'enregistrer le paramétrage réappro global",
                severity: 'error',
            });
        },
    });

    const algoDaysDisabled = useMemo(
        () => !isUserAdmin || updateAlgoDaysMut.isPending,
        [isUserAdmin, updateAlgoDaysMut.isPending]
    );

    const handleSaveAlgoDays = useCallback(() => {
        updateAlgoDaysMut.mutate(params);
    }, [params, updateAlgoDaysMut]);

    return (
        <div style={{ padding: '16px' }}>
            <Toast />
            <Typography fontWeight={600} marginBottom="8px">
                Paramétrage fenêtre de vente pour réappro
            </Typography>
            <div style={{ display: 'flex', gap: '12px', alignItems: 'center' }}>
                {[
                    REAPPRO_ALGORITHM_IDS.VMAX,
                    REAPPRO_ALGORITHM_IDS.VMJ,
                    REAPPRO_ALGORITHM_IDS.STOCK_PROJETE,
                ].map((algo) => {
                    return (
                        <TextField
                            variant="outlined"
                            size="small"
                            label={REAPPRO_ALGORITHM_BY_ID[algo]}
                            value={params.find((param) => param.id === algo)?.daysBetweenDeliveries}
                            onChange={(e) =>
                                setParams((prev) => [
                                    ...prev.filter((param) => param.id !== algo),
                                    {
                                        id: algo,
                                        daysBetweenDeliveries: parseIntOrNull(e.target.value),
                                    },
                                ])
                            }
                            type="number"
                            inputProps={{ min: 1, step: 1 }}
                        />
                    );
                })}
                <Button
                    variant="contained"
                    disabled={algoDaysDisabled}
                    onClick={handleSaveAlgoDays}
                >
                    Enregistrer
                </Button>
            </div>
        </div>
    );
};

export default ReapproAlgorithmParamsPanel;
